import axios from 'axios';
import { reverse } from 'lodash-es';

import type { EndpointResponse } from '@iconik-io/iconik-api/src/client/response';
import {
  httpPatchAssetsByAssetId,
  httpPatchCollectionsByCollectionId,
  httpPatchPlaylistsByPlaylistId,
} from '@iconik-io/iconik-api/src/services/assets';
import {
  type FilesResolutionTypeSchema,
  httpDeleteAssetsByAssetIdKeyframesByKeyframeId,
  httpDeleteCollectionsByCollectionIdKeyframesByKeyframeId,
  httpDeletePlaylistsByPlaylistIdKeyframesByKeyframeId,
  httpGetAssetsByAssetIdKeyframes,
  httpGetAssetsByAssetIdKeyframesByKeyframeId,
  httpGetAssetsByAssetIdVersionsByVersionIdKeyframes,
  httpGetCollectionsByCollectionIdKeyframes,
  httpGetCollectionsByCollectionIdKeyframesByKeyframeId,
  httpGetPlaylistsByPlaylistIdKeyframes,
  httpPatchAssetsByAssetIdKeyframesByKeyframeId,
  httpPatchCollectionsByCollectionIdKeyframesByKeyframeId,
  httpPatchPlaylistsByPlaylistIdKeyframesByKeyframeId,
  httpPostAssetsByAssetIdFilesByFileIdCaptureByMilliseconds,
  httpPostAssetsByAssetIdKeyframes,
  httpPostCollectionsByCollectionIdKeyframes,
  httpPostPlaylistsByPlaylistIdKeyframes,
} from '@iconik-io/iconik-api/src/services/files';
import {
  httpPatchJobsByJobId,
  httpPostJobs,
  type JobsJobSchemaPartial,
} from '@iconik-io/iconik-api/src/services/jobs';
import { httpPostKeyframesCollectionsByCollectionId } from '@iconik-io/iconik-api/src/services/transcode';
import {
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_COLLECTIONS,
  OBJECT_TYPE_PLAYLISTS,
  type ObjectType,
} from '@iconik-io/iconik-api/src/types/object_types';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { getOriginalFile } from '@iconik-io/shared-lib/src/utils/format';

import { getCurrentRouteParams } from '../../router/current_route_params';
import type { AppDispatch, GetState } from '../../store/types';
import type { KeyframeWithId } from '../../types/files';
import { fetchImageSourceBlob } from '../../utils/fetch_image_source_blob';
import { errorMessage, successMessage } from '../messages/thunks';
import {
  selectAssetCloudFiles,
  selectAssetFormats,
} from '../pages/asset/selectors';
import type { CoverSource } from '../pages/portals/types';
import { selectCurrentVersionId } from '../version/selectors';
import { renderImageFileToJpegBlob } from './render_image_file_to_jpeg_blob';
import {
  deleteEntityKeyframe,
  entityKeyframesLoadingUpdated,
  setEntityKeyframesIds,
} from './slice';

export const updateJob =
  (jobId: string, data: JobsJobSchemaPartial) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const writeJobs = getState().rolesReducer.userRoles.canWriteJobs;
    if (writeJobs && jobId) {
      dispatch(httpPatchJobsByJobId(jobId, data));
    }
  };

export const getEntityKeyframes =
  (entity: ObjectType, entityId: string) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const currentVersion =
      entity === OBJECT_TYPE_ASSETS
        ? selectCurrentVersionId(
            getState(),
            entityId,
            getCurrentRouteParams().versionId,
          )
        : undefined;
    dispatch(entityKeyframesLoadingUpdated({ entityId, loading: true }));

    let response;
    if (entity === OBJECT_TYPE_ASSETS && !currentVersion) {
      response = await dispatch(
        httpGetAssetsByAssetIdKeyframes(entityId, { per_page: 50 }),
      );
    }
    if (entity === OBJECT_TYPE_ASSETS && currentVersion) {
      response = await dispatch(
        httpGetAssetsByAssetIdVersionsByVersionIdKeyframes(
          entityId,
          currentVersion,
          { per_page: 50 },
        ),
      );
    }
    if (entity === OBJECT_TYPE_COLLECTIONS) {
      response = await dispatch(
        httpGetCollectionsByCollectionIdKeyframes(entityId, { per_page: 50 }),
      );
    }

    if (response) {
      const ids = response.body.objects.map((keyframe) => keyframe.id);
      dispatch(setEntityKeyframesIds({ entityId, ids: reverse(ids) }));
      dispatch(entityKeyframesLoadingUpdated({ entityId, loading: false }));
    }
  };

type DownloadKeyframeParams = {
  entity: ObjectType;
  entityId: string;
  keyframeId: string;
};

export const downloadKeyframe =
  ({ entity, entityId, keyframeId }: DownloadKeyframeParams) =>
  async (dispatch: AppDispatch): Promise<void> => {
    let promise;
    if (entity === OBJECT_TYPE_ASSETS) {
      promise = dispatch(
        httpGetAssetsByAssetIdKeyframesByKeyframeId(entityId, keyframeId, {
          content_disposition: 'attachment',
        }),
      );
    } else {
      promise = dispatch(
        httpGetCollectionsByCollectionIdKeyframesByKeyframeId(
          entityId,
          keyframeId,
        ),
      );
    }

    const keyframe = await promise.then((response) => response.body);
    if (keyframe?.url) {
      window.open(keyframe.url);
    } else {
      dispatch(errorMessage({ text: "Can't find image" }));
    }
  };

type DeleteKeyframeParams = {
  entity: ObjectType;
  entityId: string;
  keyframeId: string;
};

const getKeyframeEndpoints = (entity: ObjectType) => {
  switch (entity) {
    case OBJECT_TYPE_ASSETS:
      return {
        remove: httpDeleteAssetsByAssetIdKeyframesByKeyframeId,
        create: httpPostAssetsByAssetIdKeyframes,
        updateKeyframe: httpPatchAssetsByAssetIdKeyframesByKeyframeId,
        updateEntity: httpPatchAssetsByAssetId,
      };
    case OBJECT_TYPE_COLLECTIONS:
      return {
        remove: httpDeleteCollectionsByCollectionIdKeyframesByKeyframeId,
        create: httpPostCollectionsByCollectionIdKeyframes,
        updateKeyframe: httpPatchCollectionsByCollectionIdKeyframesByKeyframeId,
        updateEntity: httpPatchCollectionsByCollectionId,
      };
    case OBJECT_TYPE_PLAYLISTS:
      return {
        remove: httpDeletePlaylistsByPlaylistIdKeyframesByKeyframeId,
        create: httpPostPlaylistsByPlaylistIdKeyframes,
        updateKeyframe: httpPatchPlaylistsByPlaylistIdKeyframesByKeyframeId,
        updateEntity: httpPatchPlaylistsByPlaylistId,
      };
    default:
      return undefined;
  }
};

export const deleteKeyframe =
  ({ entity, entityId, keyframeId }: DeleteKeyframeParams) =>
  async (dispatch: AppDispatch): Promise<void> => {
    const endpoints = getKeyframeEndpoints(entity);
    if (!endpoints) {
      dispatch(
        errorMessage({ text: `Unsupported keyframe entity: ${entity}` }),
      );
      return;
    }
    dispatch(entityKeyframesLoadingUpdated({ entityId, loading: true }));
    await dispatch(endpoints.remove(entityId, keyframeId));
    dispatch(deleteEntityKeyframe({ entityId, id: keyframeId }));
    dispatch(entityKeyframesLoadingUpdated({ entityId, loading: false }));
  };

type CreateAndUploadKeyframeParams = {
  entity: ObjectType;
  entityId: string;
  keyframeType: 'KEYFRAME_MAP' | 'KEYFRAME' | 'POSTER';
  blob: Blob;
  resolution: FilesResolutionTypeSchema;
  skipCustomPosterUpdate?: boolean;
};

// Create, upload and update keyframe.
const createAndUploadKeyframe =
  ({
    entity,
    entityId,
    keyframeType,
    blob,
    resolution,
    skipCustomPosterUpdate,
  }: CreateAndUploadKeyframeParams) =>
  async (dispatch: AppDispatch, getState: GetState) => {
    const endpoints = getKeyframeEndpoints(entity);
    if (!endpoints) {
      dispatch(
        errorMessage({ text: `Unsupported keyframe entity: ${entity}` }),
      );
      return undefined;
    }
    // grab the keyframes storage
    const storage = getState().storageMatchingReducer.keyframesStorageMatched;
    if (!storage) {
      dispatch(errorMessage({ text: 'Keyframes matched storage not found' }));
      return undefined;
    }

    // create the keyframe
    const createKeyframeResponse = await dispatch(
      endpoints.create(entityId, {
        type: keyframeType,
        is_custom_keyframe: true,
        storage_id: storage.id,
        size: blob.size,
        resolution,
        name: `${entityId}-max-keyframe.jpg`,
        format: 'JPEG',
        content_type: 'image/jpeg',
      }),
    );

    let url: string | undefined;
    // upload the file
    if (storage.method === 'GCS') {
      try {
        const resumableUrl = await axios({
          url: createKeyframeResponse.body.upload_url,
          method: 'POST',
          headers: { 'x-goog-resumable': 'start' },
        });
        url = resumableUrl.headers.location;
        await axios({
          method: 'PUT',
          url,
          data: blob,
        });
      } catch {
        dispatch(errorMessage({ text: `Error uploading ${keyframeType}` }));
      }
    }

    if (storage.method === 'S3') {
      try {
        url = createKeyframeResponse.body.upload_url;
        await axios({
          method: 'PUT',
          url,
          data: blob,
          headers: { 'Content-Type': 'image/jpeg' },
        });
      } catch {
        dispatch(errorMessage({ text: `Error uploading ${keyframeType}` }));
      }
    }

    if (storage.method === 'AZURE') {
      try {
        url = createKeyframeResponse.body.upload_url;
        await axios({
          method: 'PUT',
          url,
          data: blob,
          headers: { 'x-ms-blob-type': 'BlockBlob' },
        });
      } catch {
        dispatch(errorMessage({ text: `Error uploading ${keyframeType}` }));
      }
    }

    // update the keyframe
    const updatedKeyframe = await dispatch(
      endpoints.updateKeyframe(entityId, createKeyframeResponse.body.id, {
        url,
        status: 'CLOSED',
        content_type: 'image/jpeg',
      }),
    );

    // update asset/collection
    if (keyframeType === 'KEYFRAME') {
      dispatch(
        endpoints.updateEntity(entityId, {
          custom_keyframe: createKeyframeResponse.body.id,
        }),
      );
    }
    if (keyframeType === 'POSTER' && !skipCustomPosterUpdate) {
      dispatch(
        endpoints.updateEntity(entityId, {
          custom_poster: createKeyframeResponse.body.id,
        }),
      );
    }

    // biome-ignore lint/plugin: a write response types its own id as optional, though the endpoint always sends one.
    return updatedKeyframe.body as KeyframeWithId;
  };

type CreateAndUploadPosterParams = {
  entity: ObjectType;
  entityId: string;
  blob: Blob;
  resolution: FilesResolutionTypeSchema;
  skipCustomPosterUpdate?: boolean;
};

const createAndUploadPoster =
  ({
    entity,
    entityId,
    blob,
    resolution,
    skipCustomPosterUpdate,
  }: CreateAndUploadPosterParams) =>
  async (dispatch: AppDispatch, getState: GetState) => {
    const job = await dispatch(
      httpPostJobs({
        object_type: 'assets',
        object_id: entityId,
        type: 'TRANSFER',
        status: 'STARTED',
        title: 'Upload Custom Keyframe',
        metadata: {
          client_name: 'Web',
        },
      }),
    );
    // create poster
    const posterKeyframe = await dispatch(
      createAndUploadKeyframe({
        entity,
        entityId,
        keyframeType: 'POSTER',
        blob,
        resolution,
        skipCustomPosterUpdate,
      }),
    );
    if (!posterKeyframe) {
      updateJob(job.body.id, {
        progress_processed: Math.round(100),
        status: 'FAILED',
      });

      return undefined;
    }

    dispatch(
      updateJob(job.body.id, {
        progress_processed: Math.round(100),
        status: 'FINISHED',
      }),
    );

    const keyframeIds =
      getState().entityKeyframes.keyframesIdsByEntityId[entityId];
    if (keyframeIds) {
      const ids = [posterKeyframe.id, ...keyframeIds];
      dispatch(setEntityKeyframesIds({ entityId, ids }));
    }

    return posterKeyframe;
  };

type UpdateStillKeyframesParams = {
  entity: ObjectType;
  entityId: string;
  blob: Blob;
  resolution: FilesResolutionTypeSchema;
};

const updateStillKeyframes =
  ({ entity, entityId, blob, resolution }: UpdateStillKeyframesParams) =>
  async (dispatch: AppDispatch) => {
    // get all entity keyframes and delete still keyframes (usually there is only one)
    const getKeyframes =
      entity === 'assets'
        ? httpGetAssetsByAssetIdKeyframes
        : httpGetCollectionsByCollectionIdKeyframes;
    const deleteKeyframes =
      entity === 'assets'
        ? httpDeleteAssetsByAssetIdKeyframesByKeyframeId
        : httpDeleteCollectionsByCollectionIdKeyframesByKeyframeId;
    const itemKeyframesResponse = await dispatch(
      getKeyframes(entityId, { per_page: 50 }),
    );
    const deletions: Promise<EndpointResponse>[] = [];
    for (const keyframe of itemKeyframesResponse.body.objects) {
      if (
        entity === 'collections' &&
        (keyframe.type === 'KEYFRAME' || keyframe.type === 'KEYFRAME_MAP')
      ) {
        deletions.push(dispatch(deleteKeyframes(entityId, keyframe.id)));
      }
      if (entity === 'assets' && keyframe.type === 'KEYFRAME') {
        deletions.push(dispatch(deleteKeyframes(entityId, keyframe.id)));
      }
    }

    await Promise.all(deletions);
    // create new still keyframe
    const keyframeData = await dispatch(
      createAndUploadKeyframe({
        entity,
        entityId,
        keyframeType: 'KEYFRAME',
        blob,
        resolution,
      }),
    );
    const ids = itemKeyframesResponse.body.objects
      .filter((item) => {
        if (entity === 'assets') {
          // keep POSTERS and KEYFRAME_MAP for assets
          return item.type !== 'KEYFRAME';
        }
        // keep POSTERS for assets
        return item.type === 'POSTER';
      })
      .map((item) => item.id);
    if (keyframeData) {
      ids.push(keyframeData.id);
    }
    dispatch(setEntityKeyframesIds({ entityId, ids: reverse(ids) }));

    return keyframeData;
  };

export const createPosterFromOriginal =
  (assetId: string, milliseconds?: number) =>
  (dispatch: AppDispatch, getState: GetState): void => {
    const state = getState();

    const { versionId } = getCurrentRouteParams();
    const formats = selectAssetFormats(state, assetId, versionId);
    const cloudFiles = selectAssetCloudFiles(state, assetId, versionId);
    const originalFile = getOriginalFile(formats, cloudFiles);

    const timeMs = milliseconds ?? 0;

    if (originalFile?.id) {
      dispatch(
        httpPostAssetsByAssetIdFilesByFileIdCaptureByMilliseconds(
          assetId,
          originalFile.id,
          String(Math.round(Number(timeMs))),
          {
            priority: 5,
          },
        ),
      ).then(() => {
        dispatch(successMessage({ text: 'Poster image scheduled' }));
      });
    } else {
      dispatch(errorMessage({ text: "Can't read file" }));
    }
  };

type UpdateKeyframeParams = {
  entity: ObjectType;
  entityId: string;
  file: File | Blob | null;
  skip?: {
    // If true, keyframe processing will be skipped.
    keyframe?: boolean;
    // If true, poster processing will be skipped.
    poster?: boolean;
    // If true, custom_poster on asset/collection entity will remain untouched.
    customPosterUpdate?: boolean;
  };
};

export const updateKeyframeAndPosterFromFile =
  ({ entity, entityId, file, skip }: UpdateKeyframeParams) =>
  async (dispatch: AppDispatch) => {
    if (!file) {
      return undefined;
    }

    dispatch(entityKeyframesLoadingUpdated({ entityId, loading: true }));
    const keyframes: KeyframeWithId[] = [];
    try {
      if (!skip?.keyframe) {
        const createdKeyframe = await renderImageFileToJpegBlob(
          file,
          960,
          720,
        ).then((keyframe) =>
          dispatch(
            updateStillKeyframes({
              entity,
              entityId,
              blob: keyframe.blob,
              resolution: keyframe.resolution,
            }),
          ),
        );
        if (createdKeyframe) {
          keyframes.push(createdKeyframe);
        }
      }

      if (!skip?.poster) {
        const createdPosterKeyframe = await renderImageFileToJpegBlob(
          file,
        ).then((poster) =>
          dispatch(
            createAndUploadPoster({
              entity,
              entityId,
              blob: poster.blob,
              resolution: poster.resolution,
              skipCustomPosterUpdate: skip?.customPosterUpdate,
            }),
          ),
        );
        if (createdPosterKeyframe) {
          keyframes.push(createdPosterKeyframe);
        }
      }
    } finally {
      dispatch(entityKeyframesLoadingUpdated({ entityId, loading: false }));
    }

    return keyframes;
  };

// TODO replace with openFileDialog
//  https://linear.app/iconik/issue/FE-3364/replace-triggering-of-open-file-dialog-with-openfiledialog-from-core
export const triggerKeyframeUpload =
  (items: SearchItemEntity[]) =>
  (dispatch: AppDispatch): void => {
    const inputFile = document.createElement('input');
    inputFile.type = 'file';
    inputFile.multiple = false;
    inputFile.accept = 'image/*';
    inputFile.click();
    inputFile.onchange = () => {
      if (inputFile.files) {
        const file = inputFile.files[0];
        items.forEach((item) => {
          dispatch(
            updateKeyframeAndPosterFromFile({
              entity: item.object_type,
              entityId: item.id,
              file,
            }),
          ).catch((error) => dispatch(errorMessage({ text: error.message })));
        });
      }
    };
  };

type CreatePosterFromVideoElementParams = {
  videoElement: HTMLVideoElement | null;
  assetId: string;
  skipKeyframeUpdate?: boolean;
  skipCustomPosterUpdate?: boolean;
};

export const createPosterFromVideoElement =
  ({
    videoElement,
    assetId,
    skipKeyframeUpdate,
    skipCustomPosterUpdate,
  }: CreatePosterFromVideoElementParams) =>
  async (dispatch: AppDispatch): Promise<KeyframeWithId | undefined> => {
    let canvas: HTMLCanvasElement | undefined;

    try {
      if (!videoElement || videoElement.videoWidth === 0) {
        return undefined;
      }

      canvas = document.createElement('canvas');
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;

      canvas.style.position = 'fixed';
      canvas.style.top = '-10000px';
      canvas.style.zIndex = '-1';

      document.body.appendChild(canvas);
      const context = canvas.getContext('2d');

      if (!context) {
        return undefined;
      }

      context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise<Blob | null>((resolve) => {
        if (!canvas) {
          resolve(null);
          return;
        }
        canvas.toBlob((data) => resolve(data), 'image/jpeg', 0.95);
      });

      if (!blob) {
        return undefined;
      }

      const keyframes = await dispatch(
        updateKeyframeAndPosterFromFile({
          entity: 'assets',
          entityId: assetId,
          file: blob,
          skip: {
            keyframe: skipKeyframeUpdate,
            customPosterUpdate: skipCustomPosterUpdate,
          },
        }),
      );

      return keyframes?.at(0);
    } catch {
      dispatch(errorMessage({ text: 'Error creating poster' }));
      return undefined;
    } finally {
      if (canvas && document.body.contains(canvas)) {
        document.body.removeChild(canvas);
      }
    }
  };

export const regenerateCollectionKeyframes =
  (collectionId: string) =>
  (dispatch: AppDispatch): Promise<unknown> =>
    dispatch(
      httpPostKeyframesCollectionsByCollectionId(collectionId, {
        force: true,
      }),
    ).then(() => {
      dispatch(successMessage({ text: 'Keyframe will be regenerated' }));
    });

// add_playlist_keyframe does not de-dupe (collections do), so the old custom keyframe
// must be removed first or several accumulate with no way to tell which is the cover.
const deleteCustomPlaylistKeyframes =
  (playlistId: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    const response = await dispatch(
      httpGetPlaylistsByPlaylistIdKeyframes(playlistId, { per_page: 50 }),
    );
    const customKeyframes = (response.body.objects ?? []).filter(
      (keyframe) =>
        keyframe.is_custom_keyframe &&
        (keyframe.type === 'POSTER' || keyframe.type === 'KEYFRAME') &&
        keyframe.id,
    );
    await Promise.all(
      customKeyframes.map((keyframe) =>
        dispatch(
          deleteKeyframe({
            entity: OBJECT_TYPE_PLAYLISTS,
            entityId: playlistId,
            keyframeId: keyframe.id,
          }),
        ),
      ),
    );
  };

// The cover is painted full-bleed, so it is stored well above its display size, but
// capped so a multi-megapixel image proxy does not become a multi-megabyte download.
const PORTAL_COVER_MAX_EDGE = 2560;
const PORTAL_COVER_THUMBNAIL_WIDTH = 960;
const PORTAL_COVER_THUMBNAIL_HEIGHT = 720;

export type PortalCoverKeyframes = {
  poster: KeyframeWithId;
  thumbnail: KeyframeWithId | undefined;
};

export const setPortalCover =
  ({ playlistId, source }: { playlistId: string; source: CoverSource }) =>
  async (dispatch: AppDispatch): Promise<PortalCoverKeyframes | undefined> => {
    try {
      await dispatch(deleteCustomPlaylistKeyframes(playlistId));
      const sourceBlob = await fetchImageSourceBlob(source);

      const posterImage = await renderImageFileToJpegBlob(
        sourceBlob,
        PORTAL_COVER_MAX_EDGE,
        PORTAL_COVER_MAX_EDGE,
      );
      const poster = await dispatch(
        createAndUploadKeyframe({
          entity: OBJECT_TYPE_PLAYLISTS,
          entityId: playlistId,
          keyframeType: 'POSTER',
          blob: posterImage.blob,
          resolution: posterImage.resolution,
        }),
      );
      if (!poster) {
        // createAndUploadKeyframe has already reported why; returning undefined
        // leaves the draft dirty so the save can be retried.
        return undefined;
      }

      // A second, small variant so cards and previews do not pull the hero-sized image.
      const thumbnailImage = await renderImageFileToJpegBlob(
        sourceBlob,
        PORTAL_COVER_THUMBNAIL_WIDTH,
        PORTAL_COVER_THUMBNAIL_HEIGHT,
      );
      const thumbnail = await dispatch(
        createAndUploadKeyframe({
          entity: OBJECT_TYPE_PLAYLISTS,
          entityId: playlistId,
          keyframeType: 'KEYFRAME',
          blob: thumbnailImage.blob,
          resolution: thumbnailImage.resolution,
        }),
      );

      return { poster, thumbnail };
    } catch {
      dispatch(errorMessage({ text: 'Error setting cover image' }));
      return undefined;
    }
  };

export const removePortalCover =
  (playlistId: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    try {
      await dispatch(deleteCustomPlaylistKeyframes(playlistId));
    } catch {
      dispatch(errorMessage({ text: 'Error removing cover image' }));
    }
  };
